/**
 * Responsibility: what the browser can actually do about a barcode.
 *
 * Scanning is done with the platform's own `BarcodeDetector` rather than a
 * decoding library. The library route costs about half a megabyte of
 * JavaScript and a WASM blob for a feature used on a phone, standing in a
 * shop, on whatever signal is available - and the browsers that have the
 * native detector are exactly the ones this gets used from.
 *
 * The consequence is that it is not everywhere: Chrome on Android has it,
 * Safari on iOS does not. That is said out loud in the UI rather than
 * papered over, because a scan button that silently does nothing is worse
 * than no scan button.
 *
 * Types are declared here because `BarcodeDetector` is not in lib.dom yet.
 */

/** One decoded code. Only the text and the format are of any use here. */
export type Barcode = {
  rawValue: string;
  format: string;
};

type Detector = {
  detect: (source: CanvasImageSource) => Promise<Barcode[]>;
};

type DetectorConstructor = {
  new (options?: { formats?: string[] }): Detector;
  getSupportedFormats?: () => Promise<string[]>;
};

/**
 * The formats worth looking for.
 *
 * `code_128` is what a PSA slab carries - the label's barcode is the
 * certificate number. The retail formats are here for sealed product,
 * where the box has an ordinary UPC or EAN. QR is included because it
 * costs nothing and plenty of packaging now carries one.
 */
export const FORMATS = ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'qr_code'];

function constructorOf(): DetectorConstructor | null {
  const found = (globalThis as { BarcodeDetector?: DetectorConstructor }).BarcodeDetector;
  return typeof found === 'function' ? found : null;
}

/** True when this browser can decode at all. Checked before offering to. */
export function scanningSupported(): boolean {
  return constructorOf() !== null && typeof navigator?.mediaDevices?.getUserMedia === 'function';
}

/** A detector, or null where the browser has none. */
export function makeDetector(): Detector | null {
  const Ctor = constructorOf();
  if (Ctor === null) return null;
  try {
    return new Ctor({ formats: FORMATS });
  } catch {
    // A browser that has the constructor but rejects the format list is
    // still worth using with its defaults.
    try {
      return new Ctor();
    } catch {
      return null;
    }
  }
}

/**
 * A PSA certificate number, if this code looks like one.
 *
 * PSA slab labels carry the cert as a plain run of digits. Returning null
 * for anything else keeps a UPC off a certificate field, where it would
 * look like a real cert and name the wrong slab forever.
 */
export function certNumberOf(code: Barcode): string | null {
  const digits = code.rawValue.trim();
  if (!/^\d{7,12}$/.test(digits)) return null;
  // A 12 or 13 digit retail code is a product, not a certificate.
  if (code.format === 'upc_a' || code.format === 'ean_13' || code.format === 'ean_8') {
    return null;
  }
  return digits;
}
